import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { 
  Phone, Mail, MapPin, Search, User, Menu, X, ChevronDown,
  Home, MessageSquare, Settings, LogOut, BarChart3, Moon, Sun
} from "lucide-react";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger 
} from "../ui/dropdown-menu";
import { GlobalSearch } from "../common/GlobalSearch";
import { NotificationBell } from "../common/NotificationBell";
import { useDarkMode } from "../../contexts/DarkModeContext";

export const Header = () => {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Gallery", path: "/gallery" },
    { name: "Announcements", path: "/announcements" },
    { name: "Events", path: "/events" },
    { name: "Courses", path: "/course-catalog" },
    { name: "Resources", path: "/resources" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <header className="sticky top-0 z-50">
      {/* Top Bar - Hidden on small screens */}
      <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-[#002147] py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center text-sm font-medium gap-2">
          <div className="hidden sm:flex items-center gap-4 lg:gap-6">
            <a href="tel:+233312234" className="flex items-center gap-2 hover:text-[#002147]/70 transition-colors">
              <Phone size={14} /> <span className="hidden lg:inline">(+233-31-22347)</span>
            </a>
            <a href="mailto:info@ycpschool.com" className="hidden md:flex items-center gap-2 hover:text-[#002147]/70 transition-colors">
              <Mail size={14} /> <span className="hidden lg:inline">info@ycpschool.com</span>
            </a>
            <span className="hidden lg:flex items-center gap-2">
              <MapPin size={14} /> Takoradi, Ghana
            </span>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto max-w-full pb-1 sm:pb-0">
            <Link 
              to="/portal?type=student" 
              className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${user?.user_type === 'student' ? 'bg-[#002147] text-white ring-2 ring-white' : 'bg-[#FF6B00] text-white hover:bg-[#E65C00]'}`}
              data-testid="student-portal-link"
            >
              Student
            </Link>
            <Link 
              to="/portal?type=parent" 
              className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${user?.user_type === 'parent' ? 'bg-[#002147] text-white ring-2 ring-white' : 'bg-[#FF6B00] text-white hover:bg-[#E65C00]'}`}
              data-testid="parent-portal-link"
            >
              Parent
            </Link>
            <Link 
              to="/portal?type=teacher" 
              className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${user?.user_type === 'teacher' ? 'bg-[#002147] text-white ring-2 ring-white' : 'bg-[#FF6B00] text-white hover:bg-[#E65C00]'}`}
              data-testid="teacher-portal-link"
            >
              Teacher
            </Link>
            <Link 
              to="/portal?type=admin" 
              className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${user?.user_type === 'admin' ? 'bg-[#002147] text-white ring-2 ring-white' : 'bg-[#FF6B00] text-white hover:bg-[#E65C00]'}`}
              data-testid="admin-portal-link"
            >
              Admin
            </Link>
            <Link 
              to="/alumni" 
              className="px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap bg-[#FF6B00] text-white hover:bg-[#E65C00]"
              data-testid="alumni-portal-link"
            >
              Alumni
            </Link>
            {(user?.user_type === 'admin' || user?.user_type === 'teacher') && (
              <Link 
                to="/analytics" 
                className="px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap bg-[#002147] text-white hover:bg-[#003366] flex items-center gap-1"
                data-testid="analytics-link"
              >
                <BarChart3 size={12} /> Analytics
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-gradient-to-r from-orange-500 to-yellow-400 shadow-md">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_schoolify-17/artifacts/jpw8rjex_image.png" 
                alt="School Logo" 
                className="w-12 h-12 object-contain"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-[#002147]">Young Christian Preparatory School/ JHS</h1>
                <p className="text-xs text-[#002147]/70">Excellence in Education Since 1970</p>
              </div>
            </Link>

            {/* Spacer */}
            <div className="flex-grow"></div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-5 ml-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-[#002147] ${
                    location.pathname === link.path ? "text-[#002147] font-bold" : "text-[#002147]/80"
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-4">
              {/* Dark Mode Toggle */}
              <button
                className="p-2 hover:bg-white/30 rounded-full transition-colors"
                onClick={toggleDarkMode}
                data-testid="dark-mode-toggle"
                title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {darkMode ? <Sun size={20} className="text-yellow-500" /> : <Moon size={20} className="text-[#002147]" />}
              </button>

              {/* Search */}
              <button 
                className="p-2 hover:bg-white/30 rounded-full transition-colors" 
                data-testid="search-btn"
                onClick={() => setSearchOpen(true)}
              >
                <Search size={20} className="text-[#002147]" />
              </button>

              {/* Global Search Modal */}
              <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

              {/* Notifications */}
              <NotificationBell />

              {/* User Menu */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 hover:bg-white/30 rounded-full p-1 pr-3 transition-colors" data-testid="user-menu-btn">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-[#002147] text-white text-sm">
                          {user.first_name?.[0]}{user.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <ChevronDown size={16} className="text-[#002147]" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="flex flex-col">
                        <span>{user.first_name} {user.last_name}</span>
                        <span className="text-xs text-gray-500 font-normal">{user.email}</span>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="dashboard-menu-item">
                      <Home size={16} className="mr-2" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/messages")} data-testid="messages-menu-item">
                      <MessageSquare size={16} className="mr-2" /> Messages
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-menu-item">
                      <Settings size={16} className="mr-2" /> Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-menu-item">
                      <LogOut size={16} className="mr-2" /> Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button 
                  onClick={() => navigate("/portal")} 
                  className="bg-[#002147] hover:bg-[#003366] text-white"
                  data-testid="login-btn"
                >
                  <User size={16} className="mr-2" /> Sign In
                </Button>
              )}

              {/* Mobile Menu Button */}
              <button 
                className="lg:hidden p-2 hover:bg-white/30 rounded-full transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X size={24} className="text-[#002147]" /> : <Menu size={24} className="text-[#002147]" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-white/20 animate-fadeIn">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block py-3 px-4 text-[#002147] hover:bg-white/20 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Header;
